import { Check, ChevronsUpDown, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { messageFor } from '@/api/errors';
import { useActivateWorkspace } from '@/api/queries/workspaces';
import { useActiveWorkspace } from '@/auth/workspace';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * Domains and mailboxes belong to a workspace. Step 17: one workspace per organisation, so this
 * only shows for an organisation that already has more than one.
 */
export function WorkspaceSwitcher() {
  const { id, name, workspaces } = useActiveWorkspace();
  const activate = useActivateWorkspace();
  if (!id || workspaces.length <= 1) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="max-w-56 gap-2 px-2"
          aria-label={`Workspace: ${name ?? ''}. Switch workspace`}
        >
          <Layers aria-hidden="true" className="text-muted-foreground" />
          <span className="truncate">{name}</span>
          <ChevronsUpDown aria-hidden="true" className="text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-60">
        <DropdownMenuLabel className="text-xs text-muted-foreground">Workspaces</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {workspaces.map((w) => (
          <DropdownMenuItem
            key={w.id}
            disabled={activate.isPending}
            onSelect={() => {
              if (w.id === id) return;
              activate.mutate(w.id, {
                onSuccess: () => toast.success(`Switched to ${w.name}`),
                onError: (err) => toast.error(messageFor(err)),
              });
            }}
          >
            <span className="flex-1 truncate">{w.name}</span>
            {w.id === id && <Check aria-hidden="true" className="text-brand-600" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
